import { Edit, ExpandMore, Save } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FieldTypes } from '../../component/CustomFormField/CustomFormField'
import { Accessibility, AlertDialog, CustomFormField, SearchTable } from '../../component/component'
import { getActiveCampaignDetails, getProductsByCampaign, updateActiveCampaignDetails } from '../../service/Service'
import { AppContext } from '../../store/AppProvider'
import { SHOW_ERROR_ALERT, SHOW_INFO_ALERT, SHOW_SUCCESS_ALERT } from '../../store/reducer/AlertNotifierReducer'
import { ErrorConstant } from '../../utils/Constants'
import { convertLocalDateString, FEATURES } from '../../utils/Utils'
import './CampaignSetup.css'

export const CampaignSetup = () => {
    const { setAlert, setShowLoader } = useContext(AppContext)
    const {
        handleSubmit,
        reset,
        control,
    } = useForm({
        defaultValues: {}
    })
    const [showTable, setShowTable] = useState(false)
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 20,
    });
    const [totalRecordCount, setTotalRecordCount] = useState(0)
    const [showLoading, setShowLoading] = useState(false)
    const [filter, setFilter] = useState('');

    const [showProducts, setShowProducts] = useState(true)
    const [editMode, setEditMode] = useState(false)
    const [confirmationData, setConfirmationData] = useState({})
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)

    const [campaign, setCampaign] = useState('')
    const [campaignData, setCampaignData] = useState({})
    const [activeCampaignData, setActiveCampaignData] = useState({})
    const [activeCampaignDataList, setActiveCampaignDataList] = useState([])

    const [fields, setFields] = useState([])
    const [productList, setProductList] = useState([])

    useEffect(() => {
        const fetchCampaignList = async () => {
            try {
                setShowLoader(true)
                const data = await getActiveCampaignDetails()
                if (data?.length) {
                    setActiveCampaignDataList(data)
                    alterCampaignList(data)
                }
            } catch (error) {
                setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.fetchCampaignDetails })
            } finally {
                setShowLoader(false)
            }
        }
        fetchCampaignList()
    }, [setAlert, setShowLoader])

    const alterCampaignList = (data) => {
        let campaignListData = {}
        const getFieldName = (param) => {
            switch (param?.toLowerCase()) {
                case 'serialnumber_field':
                    return 'Serial Number';
                case 'online_or_instore_field':
                    return 'Online / in-store';
                case 'price_paid_field':
                    return 'Price Paid';
                case 'opt_in_marketing_field':
                    return 'Opt-in Marketing';
                case 'proof_of_purchase_field':
                    return 'Proof of Purchase';
                case 'retail_associate_field':
                    return 'Retail Associate';
                case 'retailer_name_field':
                    return 'Retailer Name';
                default:
                    break;
            }
        }
        [...data]?.forEach(ele => {
            let res = []
            for (let i in ele) {
                if (i?.includes('field')) {
                    let obj = {}
                    obj.name = getFieldName(i)
                    obj.mandatory = ele[i]
                    obj.key = i
                    res.push(obj)
                }
            }
            campaignListData[ele.campaign_id] = res
        })
        setCampaignData(campaignListData)
    }

    const handleCampaignChange = (value) => {
        setCampaign(value);
        setShowLoader(true)
        if (productList?.length) setShowProducts(false)
        const selectedFields = JSON.parse(JSON.stringify(campaignData[value]))
        const existingData = {}
        selectedFields?.forEach(ele => {
            existingData[ele?.key] = ele?.mandatory
        })
        const resetData = {
            ...existingData,
            campaign_id: campaign
        }
        reset(resetData)
        setFields(selectedFields)
        showProductsByCampaign(value)
    }

    const resetValues = () => {
        reset()
        setEditMode(false)
    }

    const showUpdateConfirmation = (formData) => {
        formData.campaign_id = campaign;
        setConfirmationData(formData)
        setShowConfirmationDialog(true)
    }

    const updateCampaignDetails = async (payload) => {
        try {
            setShowLoader(true)
            setShowConfirmationDialog(false)
            const data = await updateActiveCampaignDetails(payload)
            if (Boolean(data)) {
                setEditMode(false)
                const selectedData = [...campaignData[campaign]]
                let updatedData = []
                selectedData.forEach(({ name, key, mandatory }) => {
                    updatedData.push({
                        name,
                        key,
                        mandatory: payload[key]
                    })
                })
                setCampaignData(prevState => ({
                    ...prevState,
                    [campaign]: updatedData
                }))
                setAlert({ type: SHOW_SUCCESS_ALERT, alertMessage: 'Campaign details updated successfully' })
            } else {
                setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.updateCampaignDetails })
            }
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.updateCampaignDetails })
        } finally {
            setShowLoader(false)
            setConfirmationData({})
            setShowConfirmationDialog(false)
        }
    }

    const onError = () => { }

    const filterRef = (filterVal) => {
        if (!filterVal) filterVal = ''
        setFilter(filterVal)
        showProductsByCampaign(campaign, filterVal)
    }

    const paginationRef = (updater) => {
        const newPaginatedData = updater(pagination)
        setPagination(newPaginatedData)
        if (productList?.length) showProductsByCampaign(campaign, filter, newPaginatedData)
    }

    const showProductsByCampaign = useCallback(async (campaignName, filter = '', paginationData = {}) => {
        try {
            setShowLoading(true)
            paginationData = {
                pageIndex: typeof paginationData?.pageIndex === 'number' ? paginationData?.pageIndex : pagination?.pageIndex,
                pageSize: typeof paginationData?.pageSize === 'number' ? paginationData?.pageSize : paginationData?.pageSize || pagination?.pageSize,
            }
            const selectedActiveCampaign = activeCampaignDataList?.find(ele => ele?.campaign_id === campaignName)
            const campaign_serial_id = selectedActiveCampaign?.campaign_serial_id
            setActiveCampaignData(selectedActiveCampaign)
            const { data: productListData, totalCount } = await getProductsByCampaign({ campaign: campaignName, campaign_serial_id, ...paginationData, filter })
            setShowProducts(true)
            setShowTable(true)
            setProductList(productListData || [])
            setTotalRecordCount(totalCount || 0)
        } catch (error) {
            setProductList([])
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.fetchProductsByCampaign })
        } finally {
            setShowLoading(false)
            setShowLoader(false)
        }
    }, [activeCampaignDataList, pagination?.pageIndex, pagination?.pageSize, setAlert, setShowLoader])

    const handleExportRegData = async () => {
        try {
            const campaign_serial_id = activeCampaignData?.campaign_serial_id
            setAlert({ type: SHOW_INFO_ALERT, alertMessage: "CSV Download started..." })
            const fetchedData = await getProductsByCampaign({ campaign, campaign_serial_id, ...pagination, ...filter, downloadCSV: true });
            window.open(fetchedData, "_blank");
            setAlert({ type: SHOW_SUCCESS_ALERT, alertMessage: "CSV Download successful!" })
        } catch (e) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.downloadingCSV })
        }
    }

    const campaignDropdown = Object.keys(campaignData) || []

    const columns = useMemo(
        () => [
            {
                accessorKey: 'sku_name',
                header: 'Product SKU',
                size: 150,
            },
            {
                accessorKey: 'business_category',
                header: 'Business Category',
                size: 130,
            },
        ],
        [],
    );

    const campaignValidity = `(from: ${convertLocalDateString(activeCampaignData?.campaign_start_date)} to: ${convertLocalDateString(activeCampaignData?.campaign_end_date)})`

    return (
        <div>
            <div className='header-title' >{('Campaign Setup').toUpperCase()}</div>
            <div>
                <Grid container >
                    <Grid item xs={12} md={5}>
                        <CustomFormField
                            type={FieldTypes.select}
                            name={'campaign'}
                            label="Select Campaign"
                            onDropdownChange={(val) => handleCampaignChange(val)}
                            options={[...campaignDropdown]} />
                    </Grid>
                </Grid>
                {
                    fields.length > 0 &&
                    <>
                        <div className='accordion-container'>
                            <Accordion defaultExpanded id="campaignGrid">
                                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white', fontSize: 30 }} />} sx={{ backgroundColor: '#1976d2', maxHeight: 38, '&.Mui-expanded': { backgroundColor: '#1976d2', minHeight: 48 } }}>
                                    <Typography variant="h6" style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>CAMPAIGN FIELDS&nbsp;{campaignValidity}</Typography>
                                </AccordionSummary>
                                <AccordionDetails >
                                    <form
                                        noValidate
                                        autoComplete="off">
                                        <Grid container rowSpacing={2}>
                                            {fields?.map((field, index) => (
                                                <Grid item xs={12} md={6} key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Grid item xs={12} md={9} >
                                                        <Typography style={{ fontWeight: 'bold' }}>{field.name}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={3} justifyContent={'center'} display={'flex'}>
                                                        <Controller
                                                            control={control}
                                                            name={field?.key}
                                                            defaultValue={false}
                                                            render={({ field: { onChange, value } }) => (
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox disabled={!editMode} checked={value} onChange={onChange} />
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            ))}
                                            {activeCampaignData.isEditable && <Accessibility feature={FEATURES.EDIT_CAMPAIGN_DETAILS}>
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                                    {editMode
                                                        ? <>
                                                            <Button variant="contained" startIcon={<Save />} onClick={handleSubmit(showUpdateConfirmation, onError)}>
                                                                Save
                                                            </Button>
                                                            <Button variant="outlined" style={{ marginLeft: 10, backgroundColor: 'white' }} onClick={() => resetValues()}>
                                                                Cancel
                                                            </Button>
                                                        </>
                                                        : <Button
                                                            variant="contained"
                                                            color="primary"
                                                            startIcon={<Edit />}
                                                            onClick={() => setEditMode(true)}
                                                            sx={{}}
                                                        >
                                                            Edit
                                                        </Button>
                                                    }
                                                </div>
                                            </Accessibility>}
                                        </Grid>
                                    </form>
                                </AccordionDetails>
                            </Accordion>
                        </div>
                        <div className='accordion-container'>
                            <Accordion expanded={showProducts} onChange={() => setShowProducts(!showProducts)} >
                                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white', fontSize: 30 }} />} sx={{ backgroundColor: '#1976d2', maxHeight: 38, '&.Mui-expanded': { backgroundColor: '#1976d2', minHeight: 48 } }}>
                                    <Typography variant="h6" style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>PRODUCTS ON THIS CAMPAIGN</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <SearchTable
                                        id="productTable"
                                        showTable={showTable}
                                        columns={columns}
                                        pagination={pagination}
                                        setPagination={paginationRef}
                                        totalRecordCount={totalRecordCount}
                                        showTableLoading={showLoading}
                                        globalFilter={filter}
                                        setGlobalFilter={filterRef}
                                        isEnableGlobalFilter={true}
                                        tableData={productList}
                                        handleExportData={(columns) => handleExportRegData(columns)} />
                                </AccordionDetails>
                            </Accordion>
                        </div>
                    </>
                }
            </div>
            <AlertDialog
                title='Confirm Campaign changes?'
                show={showConfirmationDialog}
                handleConfirmation={(reason) => updateCampaignDetails({ ...confirmationData, reason })}
                handleCancel={() => { setConfirmationData({}); showConfirmationDialog(false) }} />
        </div>
    )
}